import React, { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface ImageGenerateButtonProps {
  onImageGenerated: (imageUrl: string) => void;
  disabled?: boolean;
  serviceName?: string;
  serviceCategory?: string;
  description?: string;
}

export const ImageGenerateButton = ({ 
  onImageGenerated, 
  disabled = false, 
  serviceName,
  serviceCategory,
  description
}: ImageGenerateButtonProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateImage = async () => {
    if (!serviceName || serviceName.trim() === "") {
      toast({
        title: "Service name required",
        description: "Please enter a service name before generating an image.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);

    try {
      // Get user's country for better context
      const { data: { user } } = await supabase.auth.getUser();
      let userCountry = null;
      
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('country')
          .eq('id', user.id)
          .single();
        userCountry = profile?.country;
      }

      // Call the edge function
      const { data, error } = await supabase.functions.invoke('generate-service-image', {
        body: {
          serviceName: serviceName,
          serviceCategory: serviceCategory,
          description: description,
          country: userCountry,
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to generate image');
      }

      if (!data.imageData) {
        throw new Error('No image data received');
      }

      // Convert base64 to blob and create URL
      const base64Data = data.imageData;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      const imageUrl = URL.createObjectURL(blob);

      onImageGenerated(imageUrl);

      toast({
        title: "Image generated successfully!",
        description: "Your AI-generated service image has been added.",
      });
    } catch (error) {
      console.error('Error generating image:', error);
      toast({
        title: "Failed to generate image",
        description: error instanceof Error ? error.message : "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleGenerateImage}
      disabled={disabled || isGenerating}
      className={`flex flex-col items-center justify-center w-full aspect-square border-2 border-dashed rounded-lg transition-colors
        ${disabled || isGenerating
          ? 'border-gray-300 bg-gray-50 cursor-not-allowed opacity-50' 
          : 'border-purple-300 hover:border-purple-500 hover:bg-purple-50/30 cursor-pointer'
        }`}
    >
      {isGenerating ? (
        <>
          <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
          <span className="mt-2 text-sm text-gray-500">{t('services.generatingImage')}</span>
          <span className="text-xs text-gray-400">This may take a few seconds...</span>
        </>
      ) : (
        <>
          <Sparkles className={`w-8 h-8 ${disabled ? 'text-gray-400' : 'text-purple-500'}`} />
          <span className="mt-2 text-sm text-gray-500">{t('services.generateImage')}</span>
          <span className="text-xs text-gray-400">AI-powered image</span>
        </>
      )}
    </button>
  );
}; 